import { useCallback, useEffect, useState } from "react";
import Topbar from "@/components/Topbar";
import { api, formatApiErrorDetail, getBackendUrl } from "@/lib/api";
import { toast } from "sonner";
import {
  ClockCounterClockwise, CheckCircle, XCircle, ArrowLeft, IdentificationBadge,
  LinkedinLogo, FilePdf, FileArchive,
} from "@phosphor-icons/react";

/* v2.90 · Admin PE-approval queue. Shows all PEs whose profile is
   `pending` (first submission) or `pending_revision` (edit to an
   approved profile). The admin sees the full draft; when reviewing a
   revision, a side-by-side "current live vs proposed" diff of material
   fields is rendered so the decision is one click. */

const MATERIAL_FIELDS = [
  { key: "country", label: "Country" },
  { key: "region", label: "Region" },
  { key: "years_of_practice", label: "Years of practice" },
  { key: "specializations", label: "Specializations" },
  { key: "certifications", label: "Certifications" },
];

function fmt(v) {
  if (v == null || v === "") return <span className="text-gray-500">—</span>;
  if (Array.isArray(v)) {
    if (v.length === 0) return <span className="text-gray-500">—</span>;
    if (typeof v[0] === "string") return v.join(", ");
    return (
      <ul className="space-y-0.5">
        {v.map((c, i) => (
          <li key={i} className="text-xs">
            <span className="text-white">{c.name || "(unnamed)"}</span>
            {c.number && <span className="text-gray-500"> · {c.number}</span>}
            {c.issued && <span className="text-gray-500"> · {c.issued}</span>}
          </li>
        ))}
      </ul>
    );
  }
  return String(v);
}

function DiffRow({ label, before, after }) {
  const beforeStr = JSON.stringify(before);
  const afterStr = JSON.stringify(after);
  const changed = beforeStr !== afterStr;
  return (
    <div className={`grid grid-cols-1 md:grid-cols-2 gap-3 py-2 border-b border-white/5 ${changed ? "bg-[#FFCC00]/[0.04]" : ""}`}
         data-testid={`diff-row-${label.toLowerCase().replace(/\s/g,"-")}`}>
      <div>
        <div className="label-tag text-gray-500">// current live</div>
        <div className="text-sm mt-1">{fmt(before)}</div>
      </div>
      <div>
        <div className={`label-tag ${changed ? "text-[#FFCC00]" : "text-gray-500"}`}>// proposed {changed && "▲"}</div>
        <div className={`text-sm mt-1 ${changed ? "text-[#FFCC00]" : ""}`}>{fmt(after)}</div>
      </div>
    </div>
  );
}

function ProfileDetail({ item, onBack, onDecided }) {
  const [feedback, setFeedback] = useState("");
  const [busy, setBusy] = useState("");
  const draft = item.profile || {};
  const live = item.profile_live || null;
  const isRevision = item.pe_status === "pending_revision";
  const assetUrl = (ref) => ref ? `${getBackendUrl()}/api/pe/assets/${ref}` : "";

  const decide = async (kind) => {
    setBusy(kind);
    try {
      await api.post(`/admin/pe-approvals/${item.id}/${kind}`, { feedback });
      toast.success(kind === "approve" ? "PE approved · live in the directory" : "PE returned to draft with feedback");
      onDecided();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Decision failed");
    } finally {
      setBusy("");
    }
  };

  return (
    <div>
      <button onClick={onBack} className="inline-flex items-center gap-1 text-sm text-gray-400 hover:text-white mb-4" data-testid="approvals-back">
        <ArrowLeft size={14} /> back to queue
      </button>
      <div className="flex items-start gap-4">
        <div className="w-20 h-20 border border-white/10 bg-black/40 overflow-hidden flex-none">
          {draft.picture_ref
            ? <img src={assetUrl(draft.picture_ref)} alt="" className="w-full h-full object-cover" />
            : <div className="w-full h-full flex items-center justify-center text-gray-600"><IdentificationBadge size={40} weight="duotone" /></div>}
        </div>
        <div>
          <h2 className="font-display text-2xl font-black">{item.name}</h2>
          <div className="text-sm text-gray-400">{item.email}</div>
          <div className="mt-2 inline-flex items-center gap-2 border border-[#00E5FF]/40 bg-[#00E5FF]/5 px-2 py-0.5 text-xs font-mono text-[#00E5FF]">
            <ClockCounterClockwise size={12} /> {isRevision ? "REVISION PENDING" : "FIRST SUBMISSION"}
          </div>
        </div>
      </div>

      <section className="mt-6 border border-white/10 bg-[#12151C] p-5">
        <div className="label-tag mb-3">
          // material diff — {isRevision ? "live vs proposed" : "full first submission"}
        </div>
        {isRevision && live
          ? MATERIAL_FIELDS.map(({ key, label }) => (
              <DiffRow key={key} label={label} before={live[key]} after={draft[key]} />
            ))
          : MATERIAL_FIELDS.map(({ key, label }) => (
              <DiffRow key={key} label={label} before={undefined} after={draft[key]} />
            ))}
      </section>

      <section className="mt-6 border border-white/10 bg-[#12151C] p-5" data-testid="approvals-cosmetic">
        <div className="label-tag mb-3">// cosmetic snapshot (already live-visible for revisions)</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <div className="label-tag text-gray-500">// bio</div>
            <div className="mt-1 text-gray-300 whitespace-pre-wrap">{draft.bio || <span className="text-gray-500">—</span>}</div>
          </div>
          <div>
            <div className="label-tag text-gray-500">// hobbies</div>
            <div className="mt-1 text-gray-300">{draft.hobbies || <span className="text-gray-500">—</span>}</div>
          </div>
          <div>
            <div className="label-tag text-gray-500">// competencies</div>
            <div className="mt-1 text-gray-300">{(draft.competencies || []).join(", ") || <span className="text-gray-500">—</span>}</div>
          </div>
          <div>
            <div className="label-tag text-gray-500">// linkedin</div>
            <div className="mt-1">
              {draft.linkedin_url
                ? <a href={draft.linkedin_url} target="_blank" rel="noreferrer" className="text-[#00E5FF] hover:underline inline-flex items-center gap-1"><LinkedinLogo size={14} /> {draft.linkedin_url}</a>
                : <span className="text-gray-500">—</span>}
            </div>
          </div>
        </div>
        <div className="mt-5 grid grid-cols-2 gap-6">
          <div>
            <div className="label-tag text-gray-500 mb-2">// portfolio ({(draft.portfolio || []).length})</div>
            <div className="grid grid-cols-3 gap-2">
              {(draft.portfolio || []).map((it) => (
                <a key={it.ref} href={assetUrl(it.ref)} target="_blank" rel="noreferrer"
                   className="block border border-white/10 bg-black/40 p-1 hover:border-[#00E5FF]/40" title={it.title}>
                  {it.content_type?.startsWith("image/")
                    ? <img src={assetUrl(it.ref)} alt="" className="w-full h-16 object-cover" />
                    : <div className="h-16 flex items-center justify-center"><FilePdf size={24} className="text-gray-400" /></div>}
                </a>
              ))}
              {(draft.portfolio || []).length === 0 && <div className="col-span-full text-xs text-gray-500 font-mono">no portfolio</div>}
            </div>
          </div>
          <div>
            <div className="label-tag text-gray-500 mb-2">// templates ({(draft.templates || []).length})</div>
            <ul className="space-y-1">
              {(draft.templates || []).map((it) => (
                <li key={it.ref} className="text-xs font-mono">
                  <a href={assetUrl(it.ref)} target="_blank" rel="noreferrer" className="text-[#00E5FF] hover:underline inline-flex items-center gap-1">
                    <FileArchive size={12} /> {it.title}
                  </a>
                </li>
              ))}
              {(draft.templates || []).length === 0 && <div className="text-xs text-gray-500 font-mono">no templates</div>}
            </ul>
          </div>
        </div>
      </section>

      <section className="mt-6 border border-white/10 bg-[#12151C] p-5">
        <div className="label-tag mb-2">// decision + feedback</div>
        <textarea value={feedback} onChange={(e) => setFeedback(e.target.value)}
          rows={3} placeholder="Optional feedback for the PE (shown on their dashboard on reject; kept in the review log on approve)."
          className="w-full bg-black/40 border border-white/10 px-3 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none resize-none"
          data-testid="approvals-feedback" />
        <div className="mt-3 flex flex-wrap gap-3 justify-end">
          <button onClick={() => decide("reject")} disabled={!!busy}
            className="inline-flex items-center gap-2 border border-[#FF3B30]/60 text-[#FF3B30] px-4 py-2 text-sm font-mono hover:bg-[#FF3B30]/10 disabled:opacity-40"
            data-testid="approvals-reject">
            <XCircle size={16} weight="fill" /> {busy === "reject" ? "Rejecting…" : "Reject"}
          </button>
          <button onClick={() => decide("approve")} disabled={!!busy}
            className="inline-flex items-center gap-2 border border-emerald-400/60 text-emerald-400 px-4 py-2 text-sm font-mono hover:bg-emerald-400/10 disabled:opacity-40"
            data-testid="approvals-approve">
            <CheckCircle size={16} weight="fill" /> {busy === "approve" ? "Approving…" : "Approve · publish live"}
          </button>
        </div>
      </section>
    </div>
  );
}

export default function AdminApprovalsPage() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/pe-approvals");
      setList(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load queue");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div className="industrial min-h-screen bg-[#0A0C10]">
      <Topbar />
      <div className="max-w-5xl mx-auto px-6 md:px-10 py-10">
        <div className="label-tag">// admin</div>
        <h1 className="font-display text-4xl md:text-5xl font-black mt-1">PE approval queue</h1>
        <p className="text-gray-400 mt-2">Review new PE profiles and material-field edits. Cosmetic edits publish instantly and never enter this queue.</p>

        {selected ? (
          <div className="mt-8">
            <ProfileDetail item={selected} onBack={() => setSelected(null)} onDecided={() => { setSelected(null); refresh(); }} />
          </div>
        ) : (
          <div className="mt-8">
            {loading && <div className="text-sm text-gray-500 font-mono">Loading queue…</div>}
            {!loading && list.length === 0 && (
              <div className="border border-white/10 bg-white/5 p-8 text-center text-sm font-mono text-gray-400" data-testid="approvals-empty">
                Queue empty. All PE profiles are up to date.
              </div>
            )}
            {!loading && list.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {list.map((it) => (
                  <button key={it.id} onClick={() => setSelected(it)}
                    className="text-left border border-white/10 bg-[#12151C] p-4 hover:border-[#00E5FF]/60 transition"
                    data-testid={`approvals-row-${it.id}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 border border-white/10 bg-black/40 overflow-hidden flex-none">
                        {it.profile?.picture_ref
                          ? <img src={`${getBackendUrl()}/api/pe/assets/${it.profile.picture_ref}`} alt="" className="w-full h-full object-cover" />
                          : <div className="w-full h-full flex items-center justify-center text-gray-600"><IdentificationBadge size={26} weight="duotone" /></div>}
                      </div>
                      <div className="min-w-0">
                        <div className="font-display text-lg font-bold truncate">{it.name}</div>
                        <div className="text-xs text-gray-400 truncate">{it.email}</div>
                        <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-[#FFCC00]">
                          {it.pe_status === "pending_revision" ? "revision" : "new profile"}
                          · {it.completion_pct}% complete
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
